import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Message } from '@arco-design/web-react'
import i18n from '@/i18n'
import { normalizeLanguage, SupportedLanguage } from '@/i18n/language'
import { useAuthStore } from '@/stores/authStore'
import { updateUserInfoApi } from '@/api/user'

function parseUserSettings(raw?: string): Record<string, unknown> {
  if (!raw) return {}
  try {
    return (JSON.parse(raw) as Record<string, unknown>) || {}
  } catch {
    return {}
  }
}

export function useLanguagePreference() {
  const { t } = useTranslation()
  const { isAuthenticated, user, updateUser } = useAuthStore()
  const { i18n: i18nInstance } = useTranslation()
  const [loading, setLoading] = useState(false)
  const currentLanguage =
    (normalizeLanguage(i18nInstance.language) as SupportedLanguage | undefined) || 'zh-CN'

  useEffect(() => {
    if (!isAuthenticated || !user?.setting) return
    const settings = parseUserSettings(user.setting)
    const preferred = normalizeLanguage(String(settings.language || ''))
    if (preferred && preferred !== i18n.language) {
      void i18n.changeLanguage(preferred)
    }
  }, [isAuthenticated, user?.setting])

  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      if (lang === currentLanguage) return

      const previousLang = currentLanguage
      setLoading(true)
      try {
        await i18n.changeLanguage(lang)
        localStorage.setItem('i18nextLng', lang)

        if (isAuthenticated) {
          await updateUserInfoApi({ language: lang })
          if (user) {
            const settings = parseUserSettings(user.setting)
            settings.language = lang
            updateUser({ ...user, setting: JSON.stringify(settings) })
          }
          Message.success(t('common.languageSaved'))
        }
      } catch {
        await i18n.changeLanguage(previousLang)
        localStorage.setItem('i18nextLng', previousLang)
        Message.error(t('common.languageSaveFailed'))
      } finally {
        setLoading(false)
      }
    },
    [currentLanguage, isAuthenticated, t, updateUser, user]
  )

  return {
    currentLanguage,
    changeLanguage,
    loading,
  }
}

export function applyLanguageFromUserSetting(setting?: string) {
  const preferred = normalizeLanguage(String(parseUserSettings(setting).language || ''))
  if (preferred && preferred !== i18n.language) {
    void i18n.changeLanguage(preferred)
  }
}
